import React, { useEffect, useState } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { BasicUserDetails } from 'brain/data-contracts';
import { useStaffStore } from 'utils/useStaffStore';
import { useClientStore } from 'utils/useClientStore';

interface AvatarStackProps {
  participants: string[];
  currentUserId?: string;
  size?: 'sm' | 'md' | 'lg';
}

interface UserDetail {
  id: string;
  name: string;
  photoURL?: string | null;
}

export const AvatarStack: React.FC<AvatarStackProps> = ({
  participants,
  currentUserId,
  size = 'md'
}) => {
  const [userDetails, setUserDetails] = useState<{ [userId: string]: UserDetail }>({});
  const [loading, setLoading] = useState(true);

  // Get size classes
  const getSizeClasses = () => {
    switch (size) {
      case 'sm':
        return {
          avatar: 'h-6 w-6',
          text: 'text-xs',
          offset: '-ml-1.5',
          border: 'border',
          gap: 'gap-0.5'
        };
      case 'lg':
        return {
          avatar: 'h-8 w-8',
          text: 'text-xs',
          offset: '-ml-2',
          border: 'border-2',
          gap: 'gap-1'
        };
      default:
        return {
          avatar: 'h-7 w-7',
          text: 'text-xs',
          offset: '-ml-1.5',
          border: 'border',
          gap: 'gap-0.5'
        };
    }
  };

  const sizeClasses = getSizeClasses();

  // Filter out current user and limit to other participants
  const otherParticipants = participants.filter(id => id !== currentUserId);
  
  // For 2-row layout: show up to 6 avatars (3 per row) when more than 3
  const shouldUse2Rows = otherParticipants.length > 3;
  const maxAvatarsToShow = shouldUse2Rows ? Math.min(6, otherParticipants.length) : Math.min(3, otherParticipants.length);
  const visibleParticipants = otherParticipants.slice(0, maxAvatarsToShow);
  const remainingCount = Math.max(0, otherParticipants.length - maxAvatarsToShow);

  useEffect(() => {
    // Helper function to get user details using appropriate store
    const getUserDetailsFromStore = async (userId: string): Promise<{ name: string; photoURL?: string }> => {
      // Try both stores to find the user
      const { getStaffUser, fetchStaffUser } = useStaffStore.getState();
      const { getClientUser, fetchClientUser } = useClientStore.getState();
      
      // Check if user is in staff cache first
      let staffUser = getStaffUser(userId);
      if (staffUser) {
        return {
          name: staffUser.name,
          photoURL: staffUser.photoURL
        };
      }
      
      // Check if user is in client cache
      let clientUser = getClientUser(userId);
      if (clientUser) {
        return {
          name: clientUser.name,
          photoURL: clientUser.photoURL
        };
      }
      
      // If not in cache, try to fetch from staff first
      try {
        staffUser = await fetchStaffUser(userId);
        if (staffUser) {
          return {
            name: staffUser.name,
            photoURL: staffUser.photoURL
          };
        }
      } catch (error) {
        // User not found in staff store
      }
      
      // If not staff, try to fetch from clients
      try {
        clientUser = await fetchClientUser(userId);
        if (clientUser) {
          return {
            name: clientUser.name,
            photoURL: clientUser.photoURL
          };
        }
      } catch (error) {
        // User not found in client store
      }
      
      // If still not found, return unknown user
      return {
        name: 'Unknown User',
        photoURL: undefined
      };
    };

    const fetchUserDetails = async () => {
      setLoading(true);
      const details: { [userId: string]: UserDetail } = {};

      // Fetch details for visible participants using store
      for (const userId of visibleParticipants) {
        try {
          const userData = await getUserDetailsFromStore(userId);
          
          details[userId] = {
            id: userId,
            name: userData.name,
            photoURL: userData.photoURL
          };
        } catch (error) {
          details[userId] = {
            id: userId,
            name: 'Unknown User',
            photoURL: undefined
          };
        }
      }

      setUserDetails(details);
      setLoading(false);
    };

    if (visibleParticipants.length > 0) {
      fetchUserDetails();
    } else {
      setLoading(false);
    }
  }, [visibleParticipants.join(',')]);

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex">
        {Array.from({ length: Math.min(visibleParticipants.length, shouldUse2Rows ? 4 : 2) }).map((_, index) => (
          <div
            key={index}
            className={`${sizeClasses.avatar} ${index > 0 ? sizeClasses.offset : ''} bg-gray-200 rounded-full border-white ${sizeClasses.border} animate-pulse`}
          />
        ))}
      </div>
    );
  }

  if (visibleParticipants.length === 0) {
    return (
      <Avatar className={sizeClasses.avatar}>
        <AvatarFallback className="bg-gray-100 text-gray-600">
          <span className={sizeClasses.text}>G</span>
        </AvatarFallback>
      </Avatar>
    );
  }

  // Split avatars into rows for 2-row layout
  const renderAvatarGrid = () => {
    if (shouldUse2Rows) {
      const topRowCount = Math.min(3, visibleParticipants.length);
      const bottomRowCount = Math.max(0, visibleParticipants.length - 3);
      const topRow = visibleParticipants.slice(0, topRowCount);
      const bottomRow = visibleParticipants.slice(3, 6);

      return (
        <div className={`flex flex-col ${sizeClasses.gap}`}>
          {/* Top row */}
          <div className="flex">
            {topRow.map((userId, index) => renderAvatar(userId, index))}
            {/* Show +N on top row if needed and no bottom row */}
            {remainingCount > 0 && bottomRow.length === 0 && (
              <Avatar className={`${sizeClasses.avatar} ${sizeClasses.offset} border-white ${sizeClasses.border} bg-gray-100 text-gray-600`}>
                <AvatarFallback className="bg-gray-100 text-gray-600">
                  <span className={sizeClasses.text}>+{remainingCount}</span>
                </AvatarFallback>
              </Avatar>
            )}
          </div>
          
          {/* Bottom row */}
          {bottomRow.length > 0 && (
            <div className="flex">
              {bottomRow.map((userId, index) => renderAvatar(userId, index + 3, true))}
              {/* Show +N on bottom row if needed */}
              {remainingCount > 0 && (
                <Avatar className={`${sizeClasses.avatar} ${sizeClasses.offset} border-white ${sizeClasses.border} bg-gray-100 text-gray-600`}>
                  <AvatarFallback className="bg-gray-100 text-gray-600">
                    <span className={sizeClasses.text}>+{remainingCount}</span>
                  </AvatarFallback>
                </Avatar>
              )}
            </div>
          )}
        </div>
      );
    } else {
      // Single row layout
      return (
        <div className="flex">
          {visibleParticipants.map((userId, index) => renderAvatar(userId, index))}
          {/* Show +N if there are more participants */}
          {remainingCount > 0 && (
            <Avatar className={`${sizeClasses.avatar} ${sizeClasses.offset} border-white ${sizeClasses.border} bg-gray-100 text-gray-600`}>
              <AvatarFallback className="bg-gray-100 text-gray-600">
                <span className={sizeClasses.text}>+{remainingCount}</span>
              </AvatarFallback>
            </Avatar>
          )}
        </div>
      );
    }
  };

  const renderAvatar = (userId: string, index: number, isBottomRow = false) => {
    const user = userDetails[userId];
    if (!user) return null;

    return (
      <Avatar
        key={userId}
        className={`${sizeClasses.avatar} ${index > 0 && !isBottomRow ? sizeClasses.offset : isBottomRow && index > 3 ? sizeClasses.offset : ''} border-white ${sizeClasses.border} relative`}
        style={{ zIndex: 10 - (index % 3) }}
      >
        {user.photoURL ? (
          <AvatarImage 
            src={user.photoURL} 
            alt={user.name}
            className="object-cover"
            onError={(e) => {
              // Force fallback by setting src to empty
              e.currentTarget.src = '';
            }}
          />
        ) : null}
        <AvatarFallback className="bg-blue-100 text-blue-800">
          <span className={sizeClasses.text}>
            {getInitials(user.name)}
          </span>
        </AvatarFallback>
      </Avatar>
    );
  };

  return renderAvatarGrid();
};
